import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, QrCode, X } from 'lucide-react';

/**
 * 安卓版下载入口（下载链接 + 扫码弹窗）。
 *
 * 两点约定：
 *  1. **稳定文件名** `/download/molore-android.apk` 永远指最新包 —— 前端常量与二维码
 *     都不必随版本号变（避免每次发版要改三处）。
 *  2. **不按 isDesktop 隐藏**：桌面端用户正是最需要手机版的人，所以云端与桌面端都显示。
 *     （对比：桌面端自身的安装包入口才需要 `!isDesktop` 闸。）
 */
export const ANDROID_APK_URL = '/download/molore-android.apk';
export const ANDROID_QR_SRC = '/qr/android.png';

interface QrModalProps {
  open: boolean;
  onClose: () => void;
}

function AndroidQrModal({ open, onClose }: QrModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  // 必须 createPortal 到 body —— fixed z-* 救不了被困在层叠上下文里的浮层（血泪 #57）
  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="扫码下载安卓版"
    >
      <div
        className="relative w-full max-w-sm rounded-[3px] border border-[rgba(232,226,216,0.12)] bg-[#1b1815] p-7 text-center shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="关闭"
          className="absolute right-3 top-3 rounded-[2px] p-1.5 text-[#9a9286] hover:text-[#f0ebe2] hover:bg-white/5 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <p className="text-[11px] tracking-[0.3em] uppercase text-[#e0704f] mb-2">安卓版</p>
        <h3 className="text-lg font-bold text-[#f0ebe2] mb-1">扫码下载 APK</h3>
        <p className="text-xs text-[#9a9286] mb-5">用手机相机或任意扫码 App 扫描</p>

        <img
          src={ANDROID_QR_SRC}
          alt="安卓版下载二维码"
          width={220}
          height={220}
          className="mx-auto block rounded-[2px] bg-white p-2"
        />

        <a
          href={ANDROID_APK_URL}
          className="mt-5 inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-[2px] bg-[#bd4a2e] hover:bg-[#a83c22] text-[#f6ece6] text-sm font-medium transition-colors"
        >
          <Download className="w-4 h-4" />
          直接下载 APK
        </a>

        <p className="mt-3 text-[10px] leading-relaxed text-[#7d756a]">
          首次安装若提示「未知来源」，在系统设置里允许本浏览器/文件管理器安装即可。
        </p>
      </div>
    </div>,
    document.body
  );
}

interface AndroidDownloadEntryProps {
  /** 下载链接的样式（各调用点主题不同，故外传） */
  linkClassName?: string;
  /** 「扫码」按钮的样式 */
  qrClassName?: string;
  /** 链接文案 */
  label?: string;
  /** 仅显示图标 + 短文案（底栏等紧凑位） */
  compact?: boolean;
}

/**
 * 下载链接 + 扫码按钮的组合。自带弹窗状态，调用点直接丢进 JSX 即可。
 */
export function AndroidDownloadEntry({
  linkClassName,
  qrClassName,
  label = '安卓版（APK）',
  compact = false,
}: AndroidDownloadEntryProps) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  return (
    <>
      <a href={ANDROID_APK_URL} className={linkClassName} download>
        {compact ? (
          <>
            <Download className="w-3.5 h-3.5" />
            安卓版 ↓
          </>
        ) : (
          label
        )}
      </a>
      <button type="button" onClick={() => setOpen(true)} className={qrClassName} aria-label="扫码下载安卓版">
        <QrCode className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
        {compact ? '扫码' : '扫码下载'}
      </button>
      <AndroidQrModal open={open} onClose={close} />
    </>
  );
}

export default AndroidDownloadEntry;
